/**
 * What a source's own category words mean in our taxonomy (U4).
 *
 * THE SCREEN THE ERROR MESSAGE ALREADY PROMISED. A held-back row is told "Map
 * it on the source, then resolve the run again", and until this component there
 * was nothing in the admin that could: the endpoints have existed since plan
 * 013 U2 with no client at all.
 *
 * MOUNTED ON A RUN, where it receives that run's held-back phrases and their
 * counts, PLUS every mapping already saved for the source -- so a phrase this
 * run does not hold back still appears, with its picker filled and no count. `phrases` is optional so a source-scoped screen can reuse it, but no
 * such screen mounts it yet -- the optional branch is forward-looking, not a
 * path anything currently takes.
 *
 * MAPPING IS PER PHRASE, NOT PER ROW, and it is remembered: 109 headings
 * covered 2,217 Legrand rows, the largest of them 113 rows
 * (`docs/plans/data/2026-09-26-013-legrand-category-mapping.md`). That ratio is
 * why the plan defers bulk row actions rather than needing them.
 *
 * It does not follow that mapping a phrase files every row counted against it.
 * The aggregate groups held rows by their words without asking why each is held,
 * so a row waiting on a unit is counted beside one waiting on the mapping.
 */

import { Skeleton } from "@shared-ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import { CategoryPicker } from "@/features/catalog/components/category-picker";
import {
	type HeldBackPhrase,
	listSourceCategoryMappings,
	type SourceCategoryMapping,
	upsertSourceCategoryMapping,
} from "@/features/catalog/services/import-api.service";
import { apiErrorMessage } from "@/lib/api-error";

/**
 * Whether somebody has decided these words are not a category.
 *
 * A mapping row WITH no category. Distinct from `mapping === null`, which is a
 * phrase nobody has looked at -- the two look the same in an empty picker, and
 * telling them apart is the whole reason the column is nullable.
 */
function isDeclined(row: PhraseRow): boolean {
	return row.mapping !== null && row.mapping.categoryId === null;
}

/** One line of the editor: the source's words, what they mean, what they hold. */
interface PhraseRow {
	sourceCategory: string;
	/** Rows this phrase holds back in the run being viewed, if there is one. */
	rows: number | null;
	mapping: SourceCategoryMapping | null;
}

export function CategoryMappingEditor({
	sourceId,
	phrases,
	onMapped,
	disabledReason,
	unmappableRows = 0,
}: {
	sourceId: string;
	/**
	 * The run's held-back phrases with their counts for the WHOLE run.
	 *
	 * Omitted by a caller with no run to count against. No such caller exists
	 * yet. Never derived from a page of rows: the run screen fetches 100 of them
	 * (the server's own cap is 200) against runs of up to 20,000, so a count taken
	 * from there would order this list by a truncated number and put the wrong
	 * phrase first.
	 */
	phrases?: HeldBackPhrase[];
	/** Called after a mapping lands, so the caller can re-run its dry run. */
	onMapped?: () => void;
	/** Why saving is refused here, when it is. */
	disabledReason?: string | null;
	/**
	 * Rows this run holds back that carry no source category words at all.
	 *
	 * Exactly the aggregate's no-phrase bucket, and defined by the WORDS being
	 * absent rather than by why the row is held. Two different holds land in it: a
	 * row that named no category, and a row whose category came from a `slug`
	 * column and whose unit is missing -- the second names a perfectly good
	 * category, so this is not "rows that name no category".
	 *
	 * Supplied because an empty phrase list has two meanings and only one of them
	 * is "you are done": a price list with no category column holds every row this
	 * way, and saying "every phrase this run uses is already mapped" beside a tile
	 * reading "Needs mapping 5" is the false-remedy defect this screen exists to
	 * stop producing.
	 *
	 * NOT "rows a mapping cannot free", which is wider than this and not something
	 * the aggregate knows. A row held because its unit is missing while its
	 * category came from a MAPPED phrase carries those words and is counted
	 * against that phrase -- see the caveat on the counts below.
	 */
	unmappableRows?: number;
}) {
	const queryClient = useQueryClient();
	/*
	 * WHICH phrase failed, not just the message. One `useMutation` serves every
	 * row, so an unattributed error appears while the operator has moved on to a
	 * different picker and reads as a failure of that one.
	 */
	const [saveError, setSaveError] = useState<{
		sourceCategory: string;
		message: string;
	} | null>(null);
	/*
	 * A SET, because saves overlap. Holding a single phrase re-enabled the
	 * previous row while its own upsert was still in flight, which let an operator
	 * fire a second write for the SAME phrase.
	 *
	 * The upsert selects on `lower(source_category)` and then updates or inserts.
	 * Two of those racing on a phrase with no mapping yet both find nothing and
	 * both insert, colliding on the unique index -- a 23505 the operator sees as a
	 * refusal. Racing on a phrase being RE-POINTED is the quieter one: both find
	 * the row, both update, and the mapping keeps whichever landed second.
	 *
	 * Disabling every picker instead -- `save.isPending`, which the single slot
	 * itself replaced a commit earlier -- is safe but stops an operator working
	 * down 32 phrases at any pace. Per-phrase is the shape that is both.
	 */
	const [saving, setSaving] = useState<ReadonlySet<string>>(new Set());

	const mappings = useQuery({
		queryKey: ["catalog-source-category-mappings", sourceId],
		queryFn: () => listSourceCategoryMappings(sourceId),
	});

	const save = useMutation({
		mutationFn: (input: {
			sourceCategory: string;
			/** `null` declines the phrase: these words are not a category. */
			categoryId: string | null;
		}) => upsertSourceCategoryMapping(sourceId, input),
		onSuccess: () => {
			setSaveError(null);
			queryClient.invalidateQueries({
				queryKey: ["catalog-source-category-mappings", sourceId],
			});
			// The counts the caller is showing stop being true the moment this
			// lands, so re-resolving is part of saving rather than a later step.
			onMapped?.();
		},
		onError: (error, variables) =>
			setSaveError({
				sourceCategory: variables.sourceCategory,
				message: apiErrorMessage(error, "Could not save that mapping."),
			}),
		// Settled, not success: a refused save must give ITS OWN picker back, or
		// the operator cannot retry the row the error is about.
		onSettled: (_data, _error, variables) =>
			setSaving((held) => {
				const next = new Set(held);
				next.delete(variables.sourceCategory);
				return next;
			}),
	});

	const rows = useMemo<PhraseRow[]>(() => {
		// Keyed as the server matches; see `foldPhrase`.
		const fold = foldPhrase;
		const byPhrase = new Map<string, SourceCategoryMapping>();
		for (const one of mappings.data ?? []) {
			byPhrase.set(fold(one.sourceCategory), one);
		}

		const named = new Map<string, PhraseRow>();
		// A phrase with no words is a row held back for another reason entirely --
		// no category named, or a category named by slug with no unit -- and no
		// mapping can rescue it, so it is not offered a picker here. `unmappableRows`
		// is how the caller reports them.
		for (const phrase of phrases ?? []) {
			if (!phrase.sourceCategory) continue;
			named.set(fold(phrase.sourceCategory), {
				sourceCategory: phrase.sourceCategory,
				rows: phrase.rows,
				mapping: byPhrase.get(fold(phrase.sourceCategory)) ?? null,
			});
		}
		for (const [key, one] of byPhrase) {
			if (named.has(key)) continue;
			named.set(key, {
				sourceCategory: one.sourceCategory,
				rows: null,
				mapping: one,
			});
		}

		/*
		 * UNMAPPED FIRST, THEN BY ROWS DESCENDING.
		 *
		 * Not by rows alone: a mapped 113-row phrase is decided, and an unmapped
		 * 3-row phrase still holds rows out of the catalogue. Sorting by count
		 * would bury the only decision left to make underneath one already made.
		 */
		return [...named.values()].sort((a, b) => {
			const done = Number(Boolean(a.mapping)) - Number(Boolean(b.mapping));
			if (done !== 0) return done;
			const byRows = (b.rows ?? 0) - (a.rows ?? 0);
			if (byRows !== 0) return byRows;
			return a.sourceCategory.localeCompare(b.sourceCategory);
		});
	}, [mappings.data, phrases]);

	if (mappings.isError) {
		return (
			<p className="text-sm text-destructive" role="alert">
				{apiErrorMessage(
					mappings.error,
					"Could not load this source's category mappings.",
				)}
			</p>
		);
	}

	// The phrases arrived as a prop, so an in-flight mappings request is no
	// reason to hide the work the operator opened this to do.
	if (mappings.isPending && rows.length === 0) {
		return (
			<div className="grid gap-2">
				<Skeleton className="h-9 w-full" />
				<Skeleton className="h-9 w-full" />
			</div>
		);
	}

	if (rows.length === 0) {
		if (unmappableRows > 0) {
			return (
				<EmptyState
					hint="Each of those rows says what it is waiting for. A category mapping is not it."
					title={`No phrase to map, and ${unmappableRows} held row${
						unmappableRows === 1 ? "" : "s"
					} with no category words`}
				/>
			);
		}
		return (
			<EmptyState
				title={
					phrases
						? "Nothing is waiting on a category"
						: "This source has no saved mappings yet"
				}
				hint={
					phrases
						? "Every phrase this run uses is already mapped."
						: "The first import of this brand will produce the phrases to map."
				}
			/>
		);
	}

	return (
		<div className="grid gap-3">
			{disabledReason ? (
				<p className="text-sm text-muted-foreground">{disabledReason}</p>
			) : null}
			{saveError ? (
				<p className="text-sm text-destructive" role="alert">
					{saveError.sourceCategory}: {saveError.message}
				</p>
			) : null}
			{unmappableRows > 0 ? (
				// Said even when there IS work here, because clearing every phrase
				// below will not empty the group and an operator who expected it to
				// would go looking for a bug.
				<p className="text-sm text-muted-foreground">
					{unmappableRows} more held row
					{unmappableRows === 1 ? "" : "s"} name no category words to map, so
					nothing here will free {unmappableRows === 1 ? "it" : "them"}. Each
					one says what it is waiting for.
				</p>
			) : null}
			<ul className="grid gap-2">
				{rows.map((row) => (
					<li
						className="grid items-center gap-2 sm:grid-cols-[1fr_auto_1fr]"
						id={phraseAnchorId(row.sourceCategory)}
						key={row.sourceCategory}
					>
						<div className="text-sm">
							<span className="font-medium">{row.sourceCategory}</span>
							{row.rows === null ? null : (
								// "share these words", not "held back by this": the aggregate
								// groups every held row by its phrase without asking WHY each
								// one is held, so a row waiting on a unit is counted here too.
								// Mapping this phrase frees the ones waiting on the mapping.
								<span className="ml-2 text-xs text-muted-foreground">
									{row.rows} held row{row.rows === 1 ? "" : "s"} share these
									words
								</span>
							)}
						</div>
						<span aria-hidden className="hidden text-muted-foreground sm:block">
							-&gt;
						</span>
						<div className="grid gap-1">
							<CategoryPicker
								clearable={false}
								disabled={
									Boolean(disabledReason) || saving.has(row.sourceCategory)
								}
								// A name of its own: there is no visible label beside each
								// picker, and several sit on one screen.
								label={`Category for ${row.sourceCategory}`}
								onChange={(category) => {
									if (!category) return;
									setSaving((held) => new Set(held).add(row.sourceCategory));
									save.mutate({
										sourceCategory: row.sourceCategory,
										categoryId: category.id,
									});
								}}
								value={row.mapping?.categoryId ?? null}
								valueLabel={row.mapping?.categoryName ?? null}
							/>
							{isDeclined(row) ? (
								/*
								 * THE THIRD STATE. Not an empty picker: a phrase nobody has
								 * looked at and a phrase somebody decided is not a category
								 * look identical otherwise, and the second one came back as
								 * work on every run of that brand.
								 *
								 * Undone by choosing a category above, which re-points the same
								 * row -- so there is no separate undo to build, and no way to
								 * be left with a phrase that has neither state.
								 */
								<p className="text-xs text-muted-foreground">
									Recorded as not a category. Choose one above to change that.
								</p>
							) : (
								<button
									className="justify-self-start text-xs text-muted-foreground underline"
									disabled={
										Boolean(disabledReason) || saving.has(row.sourceCategory)
									}
									onClick={() => {
										setSaving((held) => new Set(held).add(row.sourceCategory));
										// `null` is the decline. The same endpoint as a mapping,
										// because it is the same decision: what these words mean.
										save.mutate({
											sourceCategory: row.sourceCategory,
											categoryId: null,
										});
									}}
									type="button"
								>
									Not a category
								</button>
							)}
						</div>
					</li>
				))}
			</ul>
		</div>
	);
}

/**
 * The fold the server matches on, in one place.
 *
 * `upsertSourceCategoryMapping` finds an existing row with
 * `lower(source_category) = lower($1)` after trimming the input, so `Frame`,
 * ` frame` and `FRAME` are ONE mapping. Every place that keys a phrase has to
 * agree with that or it will show a phrase as unmapped immediately after a save
 * that succeeded.
 */
export function foldPhrase(sourceCategory: string): string {
	return sourceCategory.trim().toLowerCase();
}

/**
 * A stable id per phrase, so a held-back row can link straight to it.
 *
 * FOLDED, not raw. The label rendered beside the picker is one representative
 * spelling chosen by the server's `min()` over the group, so a row whose own
 * text differs from it by case or a stray space would build a different id and
 * scroll to nothing. Folding both sides is what makes the lookup total.
 *
 * Encoded rather than slugged: the words are arbitrary document text -- an en
 * dash, a quotation mark, `6A - 230V AC` -- and a slug would collide two
 * phrases that differ only in punctuation, which are two separate mappings.
 */
export function phraseAnchorId(sourceCategory: string): string {
	return `phrase-${encodeURIComponent(foldPhrase(sourceCategory))}`;
}
